import { Text } from '@/components/animated-type/text';
import { AnnotatedType } from '@/components/annotated-type';
import { StoryHeader, StoryTagline, StoryTheme, StoryTitle } from '@/components/microstory/header';
import { Outro } from '@/components/microstory/outro';
import { StoriesNavigation } from '@/components/microstory/stories-navigation';
import { Theme, Themes } from '@/components/microstory/theme';
import { SeoHead } from '@/components/seo-head';
import { TheOtherClimateCrisis2023Chapter1 } from '@/components/stories/the-other-climate-crisis/chapter-1';
import { TheOtherClimateCrisis2023Chapter2 } from '@/components/stories/the-other-climate-crisis/chapter-2';
import { TheOtherClimateCrisis2023Chapter3 } from '@/components/stories/the-other-climate-crisis/chapter-3';
import { TheOtherClimateCrisis2023Chapter4 } from '@/components/stories/the-other-climate-crisis/chapter-4';
import { SurveyBackground2023 } from '@/components/survey-background-2023';
import data from '@/data/the-other-climate-crisis.json';
import { createGetServerSideProps } from '@/utils/create-get-server-side-props';
import { getStoryProps } from '@/utils/get-story-props';
import { useLayout } from '@/utils/use-layout';
import { useSharingImage } from '@/utils/use-sharing-image';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { InferGetServerSidePropsType } from 'next';

export const getServerSideProps = createGetServerSideProps(
  async () => ({ data: data as TheOtherClimateCrisisData }),
  getStoryProps('the-other-climate-crisis', 'climate-action')
);

export default function TheOtherClimateCrisis(
  props: InferGetServerSidePropsType<typeof getServerSideProps>
) {
  const { storySlug, baseUrl, data, sharingUrl, linkedStoryIds, relatedStoryId } = props;

  useLayout('white');
  useLingui();

  const sharingImage = useSharingImage('the-other-climate-crisis');

  const pageTitle = t({
    id: 'the-other-climate-crisis-2023.title',
    message: 'The other climate crisis',
  });
  const pageDescription = t({
    id: 'the-other-climate-crisis-2023.description',
    message:
      'Climate change poses an unparalleled test for humanity. The burden will fall disproportionately on the young generation. How well do young people understand the challenge?',
  });

  return (
    <>
      <SeoHead
        baseUrl={baseUrl}
        pageTitle={pageTitle}
        pageDescription={pageDescription}
        sharingImage={sharingImage}
      />

      <Theme theme={Themes.spinach} hasBackButton>
        <StoryHeader backgroundId={storySlug} css={{ backgroundPosition: 'right center' }}>
          <StoryTheme>
            <Trans id={`the-other-climate-crisis-2023.theme`}>Climate Change</Trans>
          </StoryTheme>
          <StoryTitle>{pageTitle}</StoryTitle>
          <StoryTagline>
            <Trans id={`the-other-climate-crisis-2023.tagline`}>
              <Text type="heading">
                <span>Climate change</span> poses an unparalleled test for humanity. The burden will
                fall <span>disproportionately</span> on the young generation. How well do young
                people understand the challenge?
              </Text>
            </Trans>
          </StoryTagline>
        </StoryHeader>

        <SurveyBackground2023 />

        <TheOtherClimateCrisis2023Chapter1 />
        <TheOtherClimateCrisis2023Chapter2 />
        <TheOtherClimateCrisis2023Chapter3
          dataBarChart={props.data.correct_knowledge_of_cc_younger}
        />
        <TheOtherClimateCrisis2023Chapter4 />

        <Outro
          relatedStoryId={relatedStoryId}
          sharing={{
            title: pageTitle,
            text: t({
              id: `the-other-climate-crisis.sharing.text`,
              message:
                'Climate change poses an unparalleled test for humanity. This test will fall disproportionately on the young generation. How well do young people understand this challenge?',
            }),
            url: sharingUrl,
          }}
        >
          <Trans id="the-other-climate-crisis.outro.text">
            What can be done to <AnnotatedType>improve understanding</AnnotatedType> of climate
            change around the world?
          </Trans>
        </Outro>
      </Theme>

      <StoriesNavigation storyIds={linkedStoryIds} />
    </>
  );
}
